import { FC } from "react";
import { Kbd } from "@nextui-org/kbd";
import { Link } from "@nextui-org/link";
import { Input } from "@nextui-org/input";
import {
  Navbar as NextUINavbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
} from "@nextui-org/navbar";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from "@nextui-org/dropdown";
import { link as linkStyles } from "@nextui-org/theme";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Logout from "./logout";
import TenantSelector from "./TenantSelector";

import { siteConfig } from "@/config/site";
import { ThemeSwitch } from "@/components/theme-switch";
import { SearchIcon } from "@/components/icons";
import { Logo } from "@/components/icons";
import { RtlSwitchButton } from "@/components/rtlSwitch"; // Import the RtlSwitch component
import { useDirectionLanguage } from "@/components/DirectionLanguageContext";

// Adjusted function to return role directly
const useUserRole = () => {
  return localStorage.getItem("role") || "guest";
};

// Define an interface for NavItem to help with type checking
// Export it so it can be used elsewhere if needed
export interface NavItem {
  label: string;
  href: string;
  translations: {
    en: string;
    ur: string;
  };
  roles: string[];
  hasDropdown?: boolean;
  dropdownItems?: NavItem[];
}

export const Navbar: FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { isRtl } = useDirectionLanguage();

  // Directly use the role from useUserRole
  const role = useUserRole();
  const isSuperAdmin = role === "superadmin";

  const searchInput = (
    <Input
      aria-label={t("Search")}
      classNames={{
        inputWrapper: "bg-default-100",
        input: "text-sm",
      }}
      endContent={
        <Kbd className="hidden lg:inline-block" keys={["command"]}>
          K
        </Kbd>
      }
      labelPlacement="outside"
      placeholder={t("Search...")}
      startContent={
        <SearchIcon className="text-base text-default-400 pointer-events-none flex-shrink-0" />
      }
      type="search"
    />
  );

  // Filter navItems based on user role
  const filteredNavItems = siteConfig.navItems.filter(
    (item: any) => 
      item.roles.length === 0 || 
      (role && typeof role === 'string' && item.roles.includes(role))
  );

  return (
    <NextUINavbar maxWidth="xl" position="sticky">
      <NavbarContent className="basis-1/5 sm:basis-full" justify="start">
        <NavbarBrand className="gap-3 max-w-fit">
          <Link
            className="flex justify-start items-center gap-1"
            color="foreground"
            href="/"
          >
            <Logo />
            <p className="font-bold text-inherit">Naqeer</p>
          </Link>
        </NavbarBrand>
        <div className="hidden lg:flex sm:flex md:flex gap-4 justify-start ml-2">
          {filteredNavItems.map((item: any) => {
            // Item with dropdown
            if (item.hasDropdown && item.dropdownItems?.length > 0) {
              return (
                <NavbarItem key={item.href}>
                  <Dropdown>
                    <DropdownTrigger>
                      <Link
                        className={clsx(
                          linkStyles({ color: "foreground" }),
                          pathname === item.href && "text-primary font-medium",
                          "cursor-pointer"
                        )}
                        color="foreground"
                      >
                        {isRtl ? item.translations.ur : item.translations.en}
                      </Link>
                    </DropdownTrigger>
                    <DropdownMenu aria-label={`${item.label} dropdown`}>
                      {item.dropdownItems.map((dropdownItem: any) => (
                        <DropdownItem key={dropdownItem.href}>
                          <Link
                            className={clsx(
                              linkStyles({ color: "foreground" }),
                              pathname === dropdownItem.href && "text-primary font-medium",
                              "w-full"
                            )}
                            color="foreground"
                            href={dropdownItem.href}
                          >
                            {isRtl ? dropdownItem.translations.ur : dropdownItem.translations.en}
                          </Link>
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </NavbarItem>
              );
            }
            
            // Regular item without dropdown
            return (
              <NavbarItem key={item.href}>
                <Link
                  className={clsx(
                    linkStyles({ color: "foreground" }),
                    pathname === item.href && "text-primary font-medium",
                  )}
                  color="foreground"
                  href={item.href}
                >
                  {isRtl ? item.translations.ur : item.translations.en}
                </Link>
              </NavbarItem>
            );
          })}
        </div>
      </NavbarContent>

      <NavbarContent
        className="hidden sm:flex basis-1/5 sm:basis-full"
        justify="end"
      >
        {isSuperAdmin && (
          <NavbarItem>
            <TenantSelector />
          </NavbarItem>
        )}
        <NavbarItem className="hidden sm:flex gap-2">
          <RtlSwitchButton />
          <ThemeSwitch />
          <Logout />
        </NavbarItem>
      </NavbarContent>

      <NavbarContent className="sm:hidden basis-1 pl-4" justify="end">
        <RtlSwitchButton />
        <ThemeSwitch />
        <NavbarMenuToggle />
      </NavbarContent>

      <NavbarMenu>
        {searchInput}
        <div className="mx-4 mt-2 flex flex-col gap-2">
          {filteredNavItems.map((item: any) => {
            // Item with dropdown
            if (item.hasDropdown && item.dropdownItems?.length > 0) {
              return (
                <div key={item.href} className="mb-2">
                  <Link
                    className={clsx(
                      linkStyles({ color: "foreground" }),
                      pathname === item.href && "text-primary font-medium",
                      "font-medium"
                    )}
                    color="foreground"
                    href={item.href}
                  >
                    {isRtl ? item.translations.ur : item.translations.en}
                  </Link>
                  <div className="ml-4 mt-1 flex flex-col gap-1">
                    {item.dropdownItems.map((dropdownItem: any) => (
                      <Link
                        key={dropdownItem.href}
                        className={clsx(
                          linkStyles({ color: "foreground" }),
                          pathname === dropdownItem.href && "text-primary font-medium",
                        )}
                        color="foreground"
                        href={dropdownItem.href}
                        size="sm"
                      >
                        {isRtl ? dropdownItem.translations.ur : dropdownItem.translations.en}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }
            
            // Regular item without dropdown
            return (
              <Link
                key={item.href}
                className={clsx(
                  linkStyles({ color: "foreground" }),
                  pathname === item.href && "text-primary font-medium",
                )}
                color="foreground"
                href={item.href}
                size="lg"
              >
                {isRtl ? item.translations.ur : item.translations.en}
              </Link>
            );
          })}
        </div>
      </NavbarMenu>
    </NextUINavbar>
  );
};

export default Navbar;
